import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink as RouterNavLink } from 'react-router-dom';
import styled from 'styled-components';
import SimulationPage from './pages/SimulationPage';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import './App.css';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background: var(--grad-brand);
  color: white;
  padding: 16px 24px;
  box-shadow: var(--shadow-md);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: saturate(140%);
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const Logo = styled(Link)`
  margin: 0 28px 0 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 9px;

  @media (max-width: 480px) {
    margin-right: 8px;
    font-size: 18px;
  }

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: white;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.25);
    transition: transform var(--dur-base) var(--ease-spring);
  }

  &:hover::before {
    transform: rotate(45deg) scale(1.05);
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const NavLink = styled(RouterNavLink)`
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: var(--radius-pill);
  position: relative;
  transition: background-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out);

  &:hover {
    background-color: rgba(255, 255, 255, 0.16);
    color: #fff;
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.24);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.22);
    color: #fff;
  }

  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 13.5px;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 20px;
  padding-bottom: 40px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 20;
`;

const Footer = styled.footer`
  background-color: var(--surface-0);
  padding: 28px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--ink-500);
  margin-top: auto;
  position: relative;
  z-index: 10;
  border-top: 1px solid var(--border);
`;

const FooterContent = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0;
`;

const GitHubLink = styled.a`
  color: var(--ink-500);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-pill);
  text-decoration: none;
  transition: color var(--dur-base) var(--ease-out),
    background-color var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);

  &:hover {
    color: var(--primary-color);
    background-color: var(--surface-100);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const GitHubIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"/>
  </svg>
);

function App() {
  return (
    <Router>
      <AppContainer>
        <Header>
          <Nav>
            <Logo to="/">ThreadViz</Logo>
            <NavLinks>
              <NavLink to="/" end>Home</NavLink>
              <NavLink to="/simulation">Simulation</NavLink>
              <NavLink to="/about">About</NavLink>
            </NavLinks>
          </Nav>
        </Header>
        
        <Content>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/simulation" element={<SimulationPage />} />
            <Route path="/about" element={<AboutPage />} />
          </Routes>
        </Content>
        
        <Footer>
          <FooterContent>
            Copyright &copy; {new Date().getFullYear()} CodingFreeze 
            <GitHubLink href="https://github.com/CodingFreeze/ThreadViz" target="_blank" rel="noopener noreferrer" aria-label="ThreadViz on GitHub">
              <GitHubIcon />
            </GitHubLink>
          </FooterContent>
        </Footer>
      </AppContainer>
    </Router>
  );
}

export default App;
